import { useState, useEffect, useRef } from 'react';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Fill, Style, Text } from 'ol/style';
import { unByKey } from 'ol/Observable';
import { Draw } from 'ol/interaction';

import { useProject } from '@contexts/Project.context';
import { toLonLat } from 'ol/proj';

import { formatNumber, capitalizeFirst } from '@utils/helpers';
import { formatLength, formatArea } from '@utils/measurementSystem';
import { useToast } from '@contexts/Toast.context';
import { deleteLayerByCustomId } from '@utils/map/helpers';
import { createTooltip } from '@utils/map/tooltip.overlay';
import LayerSettings from '../../sidebars/sidebarElements/LayerSettings';

/** Formats confidence to percentage with 2 decimal places */
const formatConfidence = confidence =>
	confidence == null ? null : `Confidence: ${formatNumber(confidence * 100)}%`;

/** Formats coordinates to 5 decimal places */
const formatCoordinates = coordinates => coordinates.map(coord => coord.toFixed(5));

/** Retrieves the first detection feature on pixel hovered */
const getDetectionFeature = (mapObject, { pixel }) => {
	try {
		const featureArray = mapObject.getFeaturesAtPixel(pixel);

		if (!featureArray?.length) return null;
		return featureArray?.find(feature => {
			const featureType = feature.get('featureType');
			return ['center', 'outline', 'bounding_box'].includes(featureType);
		});
	} catch (error) {
		console.warn(error);
		return null;
	}
};

/** Checks if the given event has a Draw interaction. */
const hasDrawInteraction = event => {
	const currentInteractions = event.map.getInteractions().getArray();
	return currentInteractions.some(interaction => interaction instanceof Draw);
};

//* Recieves feature and returns text style */
const textStyle = feature => {
	const featureData = feature.getProperties();
	const featureClassname = `${featureData.classname}`;
	const [featureHeightNumber, featureHeightUnit] = formatLength(featureData?.height);
	const featureHeight = `Height: ${featureHeightNumber} ${featureHeightUnit}`;

	const [featureAreaNumber, featureAreaUnit] = formatArea(featureData?.area);
	const featureArea = `Area: ${featureAreaNumber} ${featureAreaUnit}`;

	const [featureDiameterNumber, featureDiameterUnit] = formatLength(featureData?.radius * 2);
	const featureDiameter = `Diameter: ${featureDiameterNumber} ${featureDiameterUnit}`;

	const featureInfo = [
		formatConfidence(featureData?.confidence),
		featureHeightNumber && featureHeight,
		featureAreaNumber && featureArea,
		featureDiameterNumber && featureDiameter,
	];

	const featureInfoRichText = featureInfo.reduce(
		(acc, info) => {
			if (info) acc.push(info, '', '\n', '');
			return acc;
		},
		[capitalizeFirst(featureClassname), '1.2rem/1.75 Montserrat', '\n', '']
	);

	return new Style({
		text: new Text({
			text: featureInfoRichText,
			font: '1rem Montserrat',
			fill: new Fill({ color: '#fff' }),
			backgroundFill: new Fill({ color: 'rgba(0, 0, 0, 0.6)' }),
			offsetX: 16, // Adjust this value to simulate horizontal padding
			textAlign: 'left',
			padding: [8, 8, 8, 8], // Padding for the text background
		}),
	});
};

/** Responsible for rendering the object Info layer */
const DetailsLayer = ({ layerId = 'detailsLayer' }) => {
	const { addToast } = useToast();
	const { mapObject, features, toolBarVisible, defaultProjection } = useProject();

	const [layer, setLayer] = useState(null);

	const tooltipRef = useRef(null);
	const tooltip = useRef(null);
	const [tooltipContent, setTooltipContent] = useState(null);
	const interactionKeysRef = useRef(null);

	const removeInteractions = () => {
		// Make idempotent - safe to call multiple times
		if (interactionKeysRef.current) {
			interactionKeysRef.current.forEach(unByKey);
			interactionKeysRef.current = null;
		}

		if (tooltip.current && mapObject) {
			mapObject.removeOverlay(tooltip.current);
			tooltip.current = null;
		}
	};

	const addInteractions = () => {
		// Always remove existing interactions first to prevent duplicates
		removeInteractions();

		if (!tooltip.current) {
			tooltip.current = createTooltip({
				mapRef: mapObject,
				tooltipRef: tooltipRef.current,
				offset: [1, -15],
				id: 'point-info-tooltip',
			});
		}

		// Create a tooltip if mouse is over a detection feature
		const pointerMove = mapObject.on('pointermove', e => {
			const _tooltip = tooltip.current;
			if (e.dragging || !toolBarVisible || !_tooltip) return;

			const feature = getDetectionFeature(mapObject, e);
			const isDrawing = hasDrawInteraction(e);
			const mapElement = mapObject.getTargetElement();

			// FIX: If this code runs last, the cursor will be set to default even if other listeners/interactions/layers in other files set it to pointer (THIS SHOULD BE A GENERAL COMPONENT FOR THE ENTIRE MAP)
			if (feature && !isDrawing) {
				mapElement.style.cursor = 'pointer';
				mapElement.title = 'Click to copy coordinates';
			} else {
				mapElement.style.cursor = '';
				mapElement.title = '';
			}

			// Hide the tooltip if layer visible or drawing or no detection feature is found
			if (layer?.getVisible() || isDrawing || !feature) return _tooltip.setPosition(null);

			const actionByProperty = {
				confidence: confidence => formatConfidence(confidence),
				area: area => {
					const [areaNumber, areaUnit] = formatArea(area);
					return areaNumber ? `Area: ${areaNumber} ${areaUnit}` : null;
				},
				radius: radius => {
					const [diameterNumber, diameterUnit] = formatLength(radius * 2);
					return diameterNumber ? `Diameter: ${diameterNumber} ${diameterUnit}` : null;
				},
				height: height => {
					const [heightNumber, heightUnit] = formatLength(height);
					return heightNumber ? `Height: ${heightNumber} ${heightUnit}` : null;
				},
			};

			const properties = feature.getProperties();
			const classname = capitalizeFirst(`${properties.classname}`);

			// set tooltip position and content
			_tooltip.setPosition(e.coordinate);

			setTooltipContent(
				<div>
					<span className="mb-1 d-block">{classname}</span>
					<ul className="m-0 p-0 small lh-sm">
						{Object.entries(properties).map(([key, value]) => {
							const string = actionByProperty[key]?.(value);
							if (!string) return null;
							return (
								<li key={key} className="list-group-item">
									{string}
								</li>
							);
						})}
					</ul>
				</div>
			);
		});

		// Copy coordinates to clipboard when a detection feature is clicked
		const pointerClick = mapObject.on('click', async e => {
			if (e?.dragging) return;
			const feature = getDetectionFeature(mapObject, e);
			if (!feature) return;

			// Convert the coordinates from Projected Coordinate System (XY) to Geographic Coordinate System (LonLat)
			// Reverse the coordinates because we want [lat, lon] instead of [lon, lat]
			const { x, y } = feature.get('center');
			const coords = toLonLat([x, y], defaultProjection).reverse();
			const formattedCoords = formatCoordinates(coords).join(', ');

			try {
				await navigator.clipboard.writeText(formattedCoords);
				addToast({
					id: 'copy-coords-success',
					title: 'Coordinates copied to clipboard',
					message: formattedCoords,
					bg: 'success',
				});
			} catch (err) {
				console.warn('Failed to copy coordinates: ', err);
				addToast({
					id: 'copy-coords-error',
					title: 'Coordinates could not be copied to clipboard',
					message: (
						<>
							Copy them manually:
							<br />
							{formattedCoords}
						</>
					),
					autohide: false,
					bg: 'danger',
				});
			}
		});

		interactionKeysRef.current = [pointerMove, pointerClick];
	};

	useEffect(() => {
		const layerInstance = new VectorLayer({
			name: 'Details',
			zIndex: 12,
			visible: false,
			style: textStyle,
			source: new VectorSource({ features: features.center ?? [] }),
			properties: { customLayerId: layerId },
		});

		mapObject.addLayer(layerInstance);
		setLayer(layerInstance);

		return () => {
			// Ensure proper cleanup order: remove interactions first, then layer
			removeInteractions();
			deleteLayerByCustomId(mapObject, layerId);
		};
	}, [features]);

	useEffect(() => {
		// Manage interactions based on toolbar visibility
		if (!toolBarVisible) {
			removeInteractions();
		} else if (toolBarVisible && layer && tooltipRef.current) {
			addInteractions();
		}

		// Cleanup on unmount or when dependencies change
		return () => {
			removeInteractions();
		};
	}, [toolBarVisible, layer, tooltipRef.current]);

	if (!layer) return null;

	return (
		<li>
			<LayerSettings
				id={layerId}
				label={layer.get('name')}
				defaultState={layer?.getVisible()}
				onCheck={() => layer?.setVisible(true)}
				onUncheck={() => layer?.setVisible(false)}
				layer={layer}
			/>
			<div className="ol-tooltip" ref={tooltipRef}>
				{tooltipContent && tooltipContent}
			</div>
		</li>
	);
};

export default DetailsLayer;
